"use client";

import { MessageCircle, X } from "lucide-react";
import React, { createContext, useContext, useRef, useState } from "react";

import { Button } from "@components/ui/button";
import { cn } from "@lib/utils";

export type ChatPosition = "bottom-right" | "bottom-left";
export type ChatSize = "sm" | "md" | "lg" | "xl" | "full";
export type ChatMode = "minimized" | "popup" | "expanded";

const chatConfig = {
  dimensions: {
    sm: "sm:max-w-sm sm:max-h-[500px]",
    md: "sm:max-w-md sm:max-h-[600px]",
    lg: "sm:max-w-lg sm:max-h-[700px]",
    xl: "sm:max-w-xl sm:max-h-[800px]",
    full: "sm:w-full sm:h-full",
  },
  expandedDimensions: "sm:max-w-4xl sm:max-h-[90vh]",
  positions: {
    "bottom-right": "bottom-5 right-5",
    "bottom-left": "bottom-5 left-5",
  },
  chatPositions: {
    "bottom-right": "sm:bottom-[calc(100%+10px)] sm:right-0",
    "bottom-left": "sm:bottom-[calc(100%+10px)] sm:left-0",
  },
  states: {
    open: "pointer-events-auto opacity-100 visible scale-100 translate-y-0",
    closed:
      "pointer-events-none opacity-0 invisible scale-100 sm:translate-y-5",
  },
};

interface DragOffset {
  x: number;
  y: number;
}

interface ChatModeContextValue {
  chatMode: ChatMode;
  setChatMode: React.Dispatch<React.SetStateAction<ChatMode>>;
  dragOffset: DragOffset;
  setDragOffset: React.Dispatch<React.SetStateAction<DragOffset>>;
}

const ChatModeContext = createContext<ChatModeContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useChatMode() {
  const ctx = useContext(ChatModeContext);
  if (!ctx) throw new Error("useChatMode must be used within ExpandableChat");
  return ctx;
}

interface ExpandableChatProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: ChatPosition;
  size?: ChatSize;
  icon?: React.ReactNode;
  initialMode?: ChatMode;
}

const ExpandableChat: React.FC<ExpandableChatProps> = ({
  className,
  position = "bottom-right",
  size = "md",
  icon,
  initialMode = "minimized",
  children,
  ...props
}) => {
  const [chatMode, setChatMode] = useState<ChatMode>(initialMode);
  const [dragOffset, setDragOffset] = useState<DragOffset>({ x: 0, y: 0 });
  const chatRef = useRef<HTMLDivElement>(null);

  const isOpen = chatMode !== "minimized";
  const isExpanded = chatMode === "expanded";

  // Reset drag offset when mode changes
  const wrappedSetChatMode: typeof setChatMode = (value) => {
    setChatMode(value);
    setDragOffset({ x: 0, y: 0 });
  };

  const toggleChat = () =>
    setChatMode((prev) => (prev === "minimized" ? "popup" : "minimized"));

  return (
    <ChatModeContext.Provider value={{ chatMode, setChatMode: wrappedSetChatMode, dragOffset, setDragOffset }}>
      <div
        className={cn(
          `fixed ${chatConfig.positions[position]} z-50`,
          className,
        )}
        style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}
        {...props}
      >
        <div
          ref={chatRef}
          className={cn(
            "flex flex-col bg-background border sm:rounded-lg shadow-md overflow-hidden transition-all duration-250 ease-out sm:absolute sm:w-[90vw] sm:h-[80vh] fixed inset-0 w-full h-full sm:inset-auto",
            chatConfig.chatPositions[position],
            isExpanded
              ? chatConfig.expandedDimensions
              : chatConfig.dimensions[size],
            isOpen ? chatConfig.states.open : chatConfig.states.closed,
          )}
        >
          {children}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 sm:hidden"
            onClick={toggleChat}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <ExpandableChatToggle
          icon={icon}
          isOpen={isOpen}
          toggleChat={toggleChat}
        />
      </div>
    </ChatModeContext.Provider>
  );
};

ExpandableChat.displayName = "ExpandableChat";

const ExpandableChatHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex items-center justify-between p-4 border-b",
      className,
    )}
    {...props}
  />
);

ExpandableChatHeader.displayName = "ExpandableChatHeader";

const ExpandableChatBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex-grow overflow-y-auto min-h-0", className)}
    {...props}
  />
);

ExpandableChatBody.displayName = "ExpandableChatBody";

const ExpandableChatFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn("border-t p-4", className)} {...props} />;

ExpandableChatFooter.displayName = "ExpandableChatFooter";

interface ExpandableChatToggleProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  isOpen: boolean;
  toggleChat: () => void;
}

const ExpandableChatToggle: React.FC<ExpandableChatToggleProps> = ({
  className,
  icon,
  isOpen,
  toggleChat,
  ...props
}) => (
  <Button
    variant="default"
    onClick={toggleChat}
    className={cn(
      "w-14 h-14 rounded-full shadow-md flex items-center justify-center hover:shadow-lg hover:shadow-black/30 transition-all duration-300",
      className,
    )}
    {...props}
  >
    {isOpen ? (
      <X className="h-6 w-6" />
    ) : (
      icon || <MessageCircle className="h-6 w-6" />
    )}
  </Button>
);

ExpandableChatToggle.displayName = "ExpandableChatToggle";

export {
  ExpandableChat,
  ExpandableChatHeader,
  ExpandableChatBody,
  ExpandableChatFooter,
};
